import { logError } from '~shared/observability/observability';

/**
 * Money-boundary runtime guard (samera/samera#3316, OD-1).
 *
 * The generated OpenAPI types cannot catch a runtime *value* violation — a price
 * `amount` arriving as `null`, `""`, or the string `"1.290,00"` all satisfy the
 * TypeScript type `{ amount: number }` at compile time but are wrong on the wire.
 * samera-one charges guests, so a wrong value silently rendered as a price is the
 * worst failure mode. These asserts replace the old Zod `responseContract` net on
 * the money paths only: on a mismatch they throw, so the flow fails loud (→ error
 * boundary + the error reporter) instead of showing or charging a wrong amount. Keep them on
 * the money-critical responses (Session totals/deposit/item prices, Payment URL).
 */

export class MoneyContractError extends Error {
  readonly context: string;

  constructor(context: string, detail: string) {
    super(`Invalid money response (${context}): ${detail}`);
    this.name = 'MoneyContractError';
    this.context = context;
  }
}

export type Money = { amount: number; currency: string; symbol: string };

/** A well-formed money value: finite numeric amount, non-empty currency. */
export function isValidMoney(value: unknown): value is Money {
  if (!value || typeof value !== 'object') return false;
  const { amount, currency, symbol } = value as Record<string, unknown>;
  return (
    typeof amount === 'number' &&
    Number.isFinite(amount) &&
    typeof currency === 'string' &&
    currency.length > 0 &&
    typeof symbol === 'string'
  );
}

/**
 * Assert a money value is well-formed. `context` names the field/endpoint for
 * the thrown error + the error record (e.g. `"session.total"`, `"book.paymentUrl"`).
 */
export function assertMoney(
  value: unknown,
  context: string,
): asserts value is Money {
  if (!isValidMoney(value)) {
    throw new MoneyContractError(
      context,
      `expected { amount: finite number, currency: non-empty string, symbol: string }, got ${safeStringify(
        value,
      )}`,
    );
  }
}

/**
 * Assert a payment redirect URL is a non-empty http(s) URL.
 *
 * The scheme allowlist is the point, not parseability: `new URL()` happily
 * accepts `javascript:alert(1)`, `data:text/html,…` and `mailto:`. This value
 * is destined for a navigation (`window.location.href` / a form action), so a
 * parse-only check would hand a compromised or misconfigured backend a script
 * execution primitive. `http:` stays allowed for local dev against the stack.
 */
export function assertPaymentUrl(
  value: unknown,
  context: string,
): asserts value is string {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    !isSafeHttpUrl(value)
  ) {
    throw new MoneyContractError(
      context,
      `expected a non-empty http(s) URL, got ${safeStringify(value)}`,
    );
  }
}

const NAVIGABLE_PROTOCOLS = ['https:', 'http:'];

function isSafeHttpUrl(value: string): boolean {
  try {
    return NAVIGABLE_PROTOCOLS.includes(new URL(value).protocol);
  } catch {
    return false;
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Rebuild a money value field by field.
 *
 * The mappers construct their domain objects explicitly so a field the backend adds later cannot
 * reach Redux, the query cache or the GTM payload — but a nested object assigned wholesale defeats
 * that at one level down, and booking money is forwarded to GTM as a whole object
 * (build-purchase-data-layer.ts). The deleted Zod schemas stripped recursively; this restores that
 * for the one nested shape that appears everywhere (samera/samera#3319).
 */
export const toMoney = (value: Money): Money => ({
  amount: value.amount,
  currency: value.currency,
  symbol: value.symbol,
});

/**
 * `assertMoney`, but reported before it propagates — grouped under one
 * `error.fingerprint` so every malformed-amount variant lands in the same place.
 *
 * Use on money the guest is *charged*: session and booking totals, deposits, item prices.
 * Failing loud there is correct — a wrong charged amount must never render.
 *
 * Reported explicitly rather than relying on the throw surfacing by itself: the error
 * boundary's production logError is still a TODO, so an unreported contract break would be
 * invisible.
 */
export function assertMoneyReported(
  value: unknown,
  context: string,
): asserts value is Money {
  try {
    assertMoney(value, context);
  } catch (error) {
    logError(error, { kind: 'money-contract', moneyContext: context });
    throw error;
  }
}

/**
 * The non-throwing half of the pair: report a malformed money value and carry on.
 *
 * Use on money the guest is *shown but not charged* — calendar teaser prices, ERV premiums,
 * extra-service prices. One malformed teaser must not blank a hotel's whole calendar, and the
 * guest cannot act on the number without going through a charged path that does throw. Returns
 * whether the value is well-formed so the caller can decide what to render.
 */
export function reportMoney(value: unknown, context: string): value is Money {
  if (isValidMoney(value)) return true;
  logError(new MoneyContractError(context, `got ${safeStringify(value)}`), {
    kind: 'money-contract',
    moneyContext: context,
  });
  return false;
}
